import { toast } from 'react-hot-toast';
import { Phone, Mail, Copy } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Avatar } from '@/shared/components/ui/Avatar';
import type { Contact } from '../types';

interface ContactDrawerProps {
  contact: Contact | null;
  deleting?: boolean;
  onClose: () => void;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 border-b border-outline py-2 last:border-0">
      <span className="text-body-sm text-on-surface-variant">{label}</span>
      <span className="text-right text-data-md text-on-surface">{value}</span>
    </div>
  );
}

/** Quick-action — backend-real only (links + clipboard). */
function QuickAction({ icon, label, onClick, href }: { icon: React.ReactNode; label: string; onClick?: () => void; href?: string }) {
  const cls =
    'inline-flex items-center gap-1.5 rounded-card-sm border border-outline bg-surface px-2.5 py-1.5 text-body-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  if (href) {
    return (
      <a href={href} className={cls}>
        {icon} {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {icon} {label}
    </button>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function ContactDrawer({ contact, deleting, onClose, onEdit, onDelete }: ContactDrawerProps) {
  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <Drawer open={!!contact} onClose={onClose} title={contact?.name ?? 'Contact'}>
      {contact && (
        <div className="flex flex-col gap-4">
          {/* Identity header */}
          <div className="flex items-center gap-3">
            <Avatar name={contact.company || contact.name} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-data-lg font-semibold text-on-surface">{contact.name}</p>
              <p className="truncate text-body-sm text-on-surface-variant">
                {[contact.position, contact.company].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
          </div>

          {/* Quick actions — backend-real only */}
          {(contact.phone || contact.email) && (
            <div className="flex flex-wrap gap-2">
              {contact.phone && <QuickAction icon={<Phone className="h-4 w-4" />} label="Call" href={`tel:${contact.phone}`} />}
              {contact.email && <QuickAction icon={<Mail className="h-4 w-4" />} label="Email" href={`mailto:${contact.email}`} />}
              {contact.phone && <QuickAction icon={<Copy className="h-4 w-4" />} label="Copy phone" onClick={() => copy(contact.phone!, 'Phone')} />}
              {contact.email && <QuickAction icon={<Copy className="h-4 w-4" />} label="Copy email" onClick={() => copy(contact.email!, 'Email')} />}
            </div>
          )}

          <div>
            <Row label="Company" value={contact.company} />
            <Row label="Position" value={contact.position} />
            <Row
              label="Email"
              value={
                contact.email ? (
                  <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                    {contact.email}
                  </a>
                ) : null
              }
            />
            <Row
              label="Phone"
              value={
                contact.phone ? (
                  <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                    {contact.phone}
                  </a>
                ) : null
              }
            />
            <Row label="Owner" value={contact.creator_name} />
            <Row label="Created" value={fmtDate(contact.created_at)} />
          </div>

          {contact.notes && (
            <div>
              <h3 className="mb-1 text-label-caps uppercase text-on-surface-variant">Notes</h3>
              <p className="whitespace-pre-wrap text-data-md text-on-surface">{contact.notes}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button onClick={() => onEdit(contact)} className="flex-1">
              Edit Contact
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => onDelete(contact)}>
              Delete
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}
